<template>
  <div class="admin-support-widget" :class="{ expanded }">
    <div v-if="expanded" class="support-popover">
      <div class="support-popover-header">
        <div>
          <p>客服提醒</p>
          <strong>{{ alertTitle }}</strong>
        </div>
        <a-button type="text" size="small" @click="expanded = false">收起</a-button>
      </div>

      <div class="support-summary">
        <div class="summary-item urgent">
          <span>{{ handoffCount }}</span>
          <small>待接入</small>
        </div>
        <div class="summary-item">
          <span>{{ unreadTotal }}</span>
          <small>未读消息</small>
        </div>
        <div class="summary-item">
          <span>{{ conversations.length }}</span>
          <small>打开会话</small>
        </div>
      </div>

      <div class="support-actions">
        <a-button type="primary" size="small" @click="goSupportChat">进入客服台</a-button>
        <a-button size="small" @click="goAutoReplies">回复库</a-button>
      </div>

      <div v-if="topConversations.length" class="conversation-mini-list">
        <button
          v-for="item in topConversations"
          :key="item.id"
          type="button"
          class="conversation-mini"
          :class="{ urgent: item.handoff_requested }"
          @click="goSupportChat(item.id)"
        >
          <div class="conversation-mini-top">
            <strong><MemberName :name="item.user_name" :is-member="item.user_is_member" /></strong>
            <span v-if="item.handoff_requested">待接入</span>
            <a-badge :count="item.admin_unread_count" />
          </div>
          <p>{{ item.last_message || '还没有消息' }}</p>
        </button>
      </div>
      <div v-else class="support-empty">暂无待处理客服消息</div>
    </div>

    <button
      type="button"
      class="support-float-button"
      :class="{ alerting: badgeCount > 0 }"
      @click="toggleExpanded"
    >
      <CustomerServiceOutlined />
      <span>客服</span>
      <em v-if="badgeCount">{{ badgeCount > 99 ? '99+' : badgeCount }}</em>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CustomerServiceOutlined } from '@ant-design/icons-vue'
import { adminSupportApi } from '@/https/api'
import type { SupportConversation } from '@/https/types/support'
import MemberName from '@/components/common/MemberName.vue'
import { startSupportBrowserAlert, stopSupportBrowserAlert } from '@/utils/supportBrowserAlert'
import { playSupportAlertTone } from '@/utils/supportAlertTone'

const router = useRouter()
const route = useRoute()
const conversations = ref<SupportConversation[]>([])
const expanded = ref(false)
const soundEnabled = ref(true)
let pollTimer: number | undefined
let pollInFlight = false
let lastAlertKey = ''
let lastAlertCounts = { unread: 0, handoff: 0 }
let hasLoadedAlerts = false
let audioContext: AudioContext | null = null
const alertKey = 'support-floating-widget'

const handoffCount = computed(() => conversations.value.filter((item) => item.handoff_requested).length)
const unreadTotal = computed(() => conversations.value.reduce((sum, item) => sum + (item.admin_unread_count || 0), 0))
const pendingConversations = computed(() => (
  conversations.value.filter((item) => item.handoff_requested || item.admin_unread_count > 0)
))
const badgeCount = computed(() => pendingConversations.value.length)
const topConversations = computed(() => conversations.value.slice(0, 4))
const alertTitle = computed(() => {
  if (handoffCount.value > 0) return `${handoffCount.value} 个会话待接入`
  if (unreadTotal.value > 0) return `${unreadTotal.value} 条客服未读`
  return '客服状态正常'
})

const ensureAudioContext = async () => {
  if (!audioContext) {
    const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return null
    audioContext = new Ctor()
  }
  if (audioContext.state === 'suspended') {
    await audioContext.resume()
  }
  return audioContext
}

const playAlertTone = async () => {
  if (!soundEnabled.value) return
  try {
    const ctx = await ensureAudioContext()
    if (!ctx) return
    playSupportAlertTone(ctx)
  } catch (error) {
    // Browser may block audio before a user gesture.
  }
}

const prepareSoundAfterUserGesture = () => {
  if (soundEnabled.value) {
    void ensureAudioContext()
  }
  window.removeEventListener('pointerdown', prepareSoundAfterUserGesture)
  window.removeEventListener('keydown', prepareSoundAfterUserGesture)
}

const currentAlertKey = () => {
  const urgentIds = conversations.value
    .filter((item) => item.handoff_requested || item.admin_unread_count > 0)
    .map((item) => `${item.id}:${item.handoff_requested ? 1 : 0}:${item.admin_unread_count}`)
    .join('|')
  return `${badgeCount.value}-${urgentIds}`
}

const loadConversations = async () => {
  if (pollInFlight || document.hidden) return
  pollInFlight = true
  try {
    const result = await adminSupportApi.listConversations({ page: 1, size: 20, status: 'open' })
    conversations.value = result.items || []
    const unreadCount = unreadTotal.value
    const handoffCountValue = handoffCount.value
    const nextKey = currentAlertKey()
    const shouldAlert =
      hasLoadedAlerts &&
      (unreadCount > lastAlertCounts.unread || handoffCountValue > lastAlertCounts.handoff)
    if (shouldAlert && nextKey && nextKey !== lastAlertKey) {
      lastAlertKey = nextKey
      expanded.value = true
      startSupportBrowserAlert(alertKey)
      await playAlertTone()
    }
    lastAlertCounts = { unread: unreadCount, handoff: handoffCountValue }
    hasLoadedAlerts = true
    if (!lastAlertKey) lastAlertKey = nextKey
    if (badgeCount.value === 0) {
      stopSupportBrowserAlert(alertKey)
    }
  } catch (error) {
    conversations.value = []
  } finally {
    pollInFlight = false
  }
}

const toggleExpanded = () => {
  expanded.value = !expanded.value
}

const getPriorityConversationId = () => {
  return (
    conversations.value.find((item) => item.handoff_requested)?.id ||
    conversations.value.find((item) => item.admin_unread_count > 0)?.id ||
    conversations.value[0]?.id
  )
}

const goSupportChat = (conversationId?: number) => {
  expanded.value = false
  stopSupportBrowserAlert(alertKey)
  const targetId = conversationId || getPriorityConversationId()
  router.push({
    name: 'admin-support-chat',
    query: targetId ? { conversation_id: String(targetId) } : undefined
  })
}

const goAutoReplies = () => {
  expanded.value = false
  router.push('/admin/support/auto-replies')
}

const stopPolling = () => {
  if (pollTimer) window.clearTimeout(pollTimer)
  pollTimer = undefined
}

const schedulePolling = () => {
  stopPolling()
  if (document.hidden) return
  pollTimer = window.setTimeout(async () => {
    pollTimer = undefined
    await loadConversations()
    schedulePolling()
  }, expanded.value ? 10000 : 30000)
}

const handleVisibilityChange = () => {
  if (document.hidden) {
    stopPolling()
    return
  }
  void loadConversations()
  schedulePolling()
}

onMounted(async () => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('pointerdown', prepareSoundAfterUserGesture, { passive: true })
  window.addEventListener('keydown', prepareSoundAfterUserGesture)
  window.addEventListener('support-alerts-updated', loadConversations)
  await loadConversations()
  schedulePolling()
})

watch(badgeCount, (count) => {
  if (count === 0) {
    expanded.value = false
  }
})

watch(() => route.path, () => {
  expanded.value = false
  schedulePolling()
})

onBeforeUnmount(() => {
  stopPolling()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  window.removeEventListener('pointerdown', prepareSoundAfterUserGesture)
  window.removeEventListener('keydown', prepareSoundAfterUserGesture)
  window.removeEventListener('support-alerts-updated', loadConversations)
  stopSupportBrowserAlert(alertKey)
  if (audioContext) {
    audioContext.close().catch(() => {})
  }
})
</script>

<style scoped lang="less">
.admin-support-widget {
  position: fixed;
  right: 22px;
  top: 50%;
  bottom: auto;
  z-index: 1300;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  pointer-events: none;
  transform: translateY(-50%);
}

.support-float-button,
.support-popover {
  pointer-events: auto;
}

.support-float-button {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 52px;
  min-width: 52px;
  height: 52px;
  padding: 0;
  border: 1px solid rgba(56, 189, 248, 0.52);
  border-radius: 50%;
  background:
    radial-gradient(120% 160% at 0 0, rgba(34, 197, 94, 0.28), transparent 58%),
    linear-gradient(135deg, rgba(14, 165, 233, 0.94), rgba(15, 23, 42, 0.96));
  color: #f8fafc;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
  box-shadow:
    0 18px 44px rgba(2, 6, 23, 0.42),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.support-float-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 22px 54px rgba(2, 6, 23, 0.52),
    0 0 22px rgba(56, 189, 248, 0.3);
}

.support-float-button.alerting {
  border-color: rgba(248, 113, 113, 0.9);
  animation: support-float-pulse 1s ease-in-out infinite;
  box-shadow:
    0 18px 44px rgba(2, 6, 23, 0.42),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset,
    0 0 18px rgba(248, 113, 113, 0.34);
}

.support-float-button :deep(.anticon) {
  font-size: 19px;
  line-height: 1;
}

.support-float-button span {
  font-size: 10px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}

.support-float-button em {
  position: absolute;
  top: -9px;
  right: -7px;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ef4444;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  line-height: 24px;
  box-shadow: 0 8px 18px rgba(239, 68, 68, 0.42);
}

@keyframes support-float-pulse {
  0%,
  100% {
    transform: translateY(0);
    box-shadow:
      0 18px 44px rgba(2, 6, 23, 0.42),
      0 0 0 1px rgba(255, 255, 255, 0.08) inset,
      0 0 16px rgba(248, 113, 113, 0.22);
  }

  50% {
    transform: translateY(-2px);
    box-shadow:
      0 24px 56px rgba(2, 6, 23, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.08) inset,
      0 0 28px rgba(248, 113, 113, 0.54);
  }
}

.support-popover {
  width: 360px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 16px;
  background:
    radial-gradient(420px 220px at 0 0, rgba(56, 189, 248, 0.18), transparent 62%),
    rgba(15, 23, 42, 0.96);
  box-shadow:
    0 26px 70px rgba(2, 6, 23, 0.54),
    0 0 0 1px rgba(255, 255, 255, 0.06) inset;
  color: #e5f2ff;
  backdrop-filter: blur(16px);
}

.support-popover-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 12px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.14);
}

.support-popover-header p {
  margin: 0 0 4px;
  color: #38bdf8;
  font-size: 12px;
}

.support-popover-header strong {
  color: #f8fafc;
  font-size: 16px;
}

.support-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 12px 14px;
}

.summary-item {
  display: grid;
  gap: 2px;
  min-height: 58px;
  place-items: center;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.72);
}

.summary-item span {
  color: #f8fafc;
  font-size: 20px;
  font-weight: 850;
}

.summary-item small {
  color: #aebdd1;
  font-size: 12px;
}

.summary-item.urgent span {
  color: #fb7185;
}

.support-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 14px 12px;
}

.conversation-mini-list {
  display: grid;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
  padding: 0 14px 14px;
}

.conversation-mini {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px;
  background: rgba(2, 6, 23, 0.38);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.conversation-mini.urgent {
  border-color: rgba(248, 113, 113, 0.5);
  background: rgba(127, 29, 29, 0.24);
}

.conversation-mini-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.conversation-mini-top strong {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #f8fafc;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.conversation-mini-top span {
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(248, 113, 113, 0.16);
  color: #fecdd3;
  font-size: 12px;
}

.conversation-mini p,
.support-empty {
  margin: 6px 0 0;
  overflow: hidden;
  color: #b6c5d8;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.support-empty {
  margin: 0;
  padding: 4px 14px 16px;
  text-align: center;
}

@media (max-width: 768px) {
  .admin-support-widget {
    top: auto;
    right: 16px;
    bottom: 18px;
    transform: none;
  }

  .support-popover {
    width: min(340px, calc(100vw - 32px));
  }
}
</style>
